import { useState, useCallback, useRef, useEffect } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useTransform,
  useReducedMotion,
  animate,
  type MotionValue,
} from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
// Brand lemon mark rebuilt as a pure vector so it stays sharp at any size
// and can share a color language with the halo droplets.

type Phase = "idle" | "burst" | "reveal";

const HOLD_MS = 700;
// easeOutCubic — pressure builds fast, softens into completion so the squeeze
// feels like a real physical gesture rather than a linear timer.
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const reduceMotion = useReducedMotion();

  const [phase, setPhase] = useState<Phase>("idle");
  const [progress, setProgress] = useState(0);
  const [hovering, setHovering] = useState(false);
  const hapticStepRef = useRef(0);

  // `pressure` is the source of truth (0..1). During press it is driven
  // imperatively via rAF with an eased curve; on release we hand it to a
  // soft spring via `animate()` so the two phases can use *different*
  // physics — snappy in, bouncy out.
  const pressure = useMotionValue(0);

  // Body follows pressure through a stiff/controlled spring — near-immediate,
  // stable, no visible wobble under load.
  const bodySpring = useSpring(pressure, {
    stiffness: 420,
    damping: 34,
    mass: 0.7,
    restDelta: 0.001,
  });

  // Interior highlight lags behind with softer physics — creates a cascade
  // read: the peel deforms, then the light on it catches up.
  const highlightSpring = useSpring(pressure, {
    stiffness: 180,
    damping: 20,
    mass: 1,
    restDelta: 0.001,
  });

  // Droplet spring matches the original juice droplet's spring physics.
  const dropletSpring = useSpring(pressure, {
    // Softer, higher-damping spring for a silky flight — droplets ease outward
    // without any residual jitter or bounce.
    stiffness: 130,
    damping: 26,
    mass: 1.1,
    restDelta: 0.0005,
  });

  // squash & stretch (transform-only, 60fps).
  const scaleY = useTransform(bodySpring, (v) => 1 - v * 0.3);
  const scaleX = useTransform(bodySpring, (v) => 1 + v * 0.18);
  // Micro-rotation breaks perfect symmetry — a real fruit never squeezes
  // perfectly axis-aligned.
  const rotate = useTransform(bodySpring, (v) => v * -2.4);
  const glow = useTransform(
    bodySpring,
    (v) =>
      `drop-shadow(0 30px 60px hsl(19 100% 50% / ${0.18 + v * 0.35})) drop-shadow(0 0 ${20 + v * 60}px hsl(48 95% 62% / ${0.25 + v * 0.55}))`
  );

  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const releaseAnimRef = useRef<{ stop: () => void } | null>(null);

  const stopLoop = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const complete = useCallback(() => {
    setPhase("burst");
    setTimeout(() => setPhase("reveal"), 260);
    // Signal the next page to render a fading-out golden veil so the transition
    // is one continuous cinematic curtain instead of a hard route swap.
    setTimeout(() => {
      try {
        sessionStorage.setItem("landing:transition", String(Date.now()));
      } catch {}
      navigate("/home");
    }, 1050);
  }, [navigate]);

  const startHold = useCallback(() => {
    if (phase !== "idle" || startRef.current != null) return;
    // Cancel any in-flight release bounce so a fresh press starts clean.
    releaseAnimRef.current?.stop();
    releaseAnimRef.current = null;
    startRef.current = performance.now();
    hapticStepRef.current = 0;
    const tick = (now: number) => {
      const t0 = startRef.current;
      if (t0 == null) return;
      const raw = Math.min(1, (now - t0) / HOLD_MS);
      // Feed the spring an eased target so the deformation accelerates in
      // and softens out — never a constant-velocity ramp.
      const eased = easeOut(raw);
      pressure.set(eased);
      setProgress(eased);
      // Tiny haptic ticks at 33/66/100% — a subtle physical rhythm on mobile.
      const step = eased >= 1 ? 3 : eased >= 0.66 ? 2 : eased >= 0.33 ? 1 : 0;
      if (step > hapticStepRef.current) {
        hapticStepRef.current = step;
        try {
          navigator.vibrate?.(step === 3 ? 22 : 8);
        } catch {}
      }
      if (raw >= 1) {
        startRef.current = null;
        stopLoop();
        complete();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [phase, pressure, stopLoop, complete]);

  const endHold = useCallback(() => {
    if (startRef.current == null) return;
    startRef.current = null;
    stopLoop();
    setProgress(0);
    // Release phase uses a softer, more elastic spring so the fruit visibly
    // rebounds with 1–2 gentle oscillations before settling.
    releaseAnimRef.current = animate(pressure, 0, {
      type: "spring",
      stiffness: 140,
      damping: 8,
      mass: 1,
      restDelta: 0.001,
    });
  }, [pressure, stopLoop]);

  const onPressStart = useCallback(
    (e: React.PointerEvent) => {
      if (phase !== "idle") return;
      e.preventDefault();
      (e.target as Element).setPointerCapture?.(e.pointerId);
      startHold();
    },
    [phase, startHold]
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === " " || e.key === "Enter") && phase === "idle") {
      e.preventDefault();
      startHold();
    }
  };

  useEffect(() => {
    return () => {
      stopLoop();
      releaseAnimRef.current?.stop();
    };
  }, [stopLoop]);

  const isIdle = phase === "idle";

  return (
    <div
      className="relative min-h-[100dvh] w-full overflow-hidden text-foreground"
      style={{
        background:
          "radial-gradient(120% 90% at 50% 40%, hsl(var(--background)) 0%, hsl(48 55% 96%) 55%, hsl(35 45% 92%) 100%)",
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 40% at 50% 55%, hsl(48 95% 62% / 0.08) 0%, transparent 70%)",
        }}
      />

      {/* Reveal overlay */}
      <AnimatePresence>
        {(phase === "burst" || phase === "reveal") && (
          <motion.div
            key="reveal"
            className="fixed inset-0 z-[60] pointer-events-none"
            initial={{ clipPath: "circle(0% at 50% 50%)", opacity: 1 }}
            animate={{ clipPath: "circle(160% at 50% 50%)", opacity: 1 }}
            transition={{ duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
            style={{
              background:
                "radial-gradient(circle at 50% 50%, hsl(48 95% 70%) 0%, hsl(48 92% 58%) 45%, hsl(35 90% 52%) 100%)",
            }}
          />
        )}
      </AnimatePresence>

      {/* Burst flash */}
      <AnimatePresence>
        {phase === "burst" && (
          <motion.div
            key="flash"
            className="fixed inset-0 z-[55] pointer-events-none bg-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.35, 0] }}
            transition={{ duration: 0.55, times: [0, 0.35, 1], ease: "easeOut" }}
          />
        )}
      </AnimatePresence>

      {/* Language auto-detected from the visitor's system — no manual toggle */}

      <main className="relative z-10 min-h-[100dvh] w-full flex flex-col items-center px-6 pt-[8vh] sm:pt-[10vh] pb-[6vh] sm:pb-[8vh]">
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-6 sm:mb-8"
        >
          <motion.span
            initial={{ opacity: 0, letterSpacing: "0.32em" }}
            animate={{ opacity: 1, letterSpacing: "0.22em" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="inline-block text-[10px] font-semibold uppercase tracking-[0.22em] text-primary mb-4"
          >
            Lemons in the Room · Insights
          </motion.span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground tracking-tight leading-[1.05] max-w-xl">
            <motion.span
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
              className="inline-block"
            >
              {t("Un momento di sollievo,", "A moment of relief,", "Un momento de alivio,")}
            </motion.span>
            <br />
            <motion.span
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: 0.28 }}
              className="inline-block text-primary/90"
            >
              {t("a portata di gesto.", "one gesture away.", "al alcance de un gesto.")}
            </motion.span>
          </h1>
        </motion.div>

        <div className="flex-1 flex items-center justify-center w-full">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            className="relative flex items-center justify-center"
          >
          <motion.div
            animate={
              isIdle && progress === 0 && !reduceMotion
                ? { y: [0, -10, 0] }
                : { y: 0 }
            }
            transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
            className="relative"
          >
            {/* Pressure ring */}
            <svg
              aria-hidden
              className="absolute inset-0 m-auto pointer-events-none"
              width="340"
              height="340"
              viewBox="0 0 340 340"
              style={{
                opacity: progress > 0 ? 0.95 : hovering ? 0.6 : 0.35,
                transition: "opacity 200ms ease",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                position: "absolute",
              }}
            >
              <motion.circle
                cx="170"
                cy="170"
                r="158"
                fill="none"
                stroke="hsl(48 95% 62% / 0.25)"
                strokeWidth="1.5"
                strokeDasharray="2 6"
                animate={
                  isIdle && !reduceMotion
                    ? { rotate: 360 }
                    : { rotate: 0 }
                }
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                style={{ transformOrigin: "170px 170px" }}
              />
              <circle
                cx="170"
                cy="170"
                r="158"
                fill="none"
                stroke="hsl(19 100% 55%)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray={`${Math.PI * 2 * 158}`}
                strokeDashoffset={`${Math.PI * 2 * 158 * (1 - progress)}`}
                transform="rotate(-90 170 170)"
                style={{ transition: "stroke-dashoffset 60ms linear" }}
              />
            </svg>

            <motion.button
              type="button"
              aria-label={t(
                "Tieni premuto il limone per entrare",
                "Press and hold the lemon to enter", "Mantén presionado el limón para entrar"
              )}
              onPointerDown={onPressStart}
              onPointerUp={endHold}
              onPointerLeave={endHold}
              onPointerCancel={endHold}
              onKeyDown={onKeyDown}
              onKeyUp={endHold}
              onMouseEnter={() => setHovering(true)}
              onMouseLeave={() => setHovering(false)}
              disabled={!isIdle}
              className="relative block bg-transparent border-0 p-0 touch-none select-none focus-visible:outline-none rounded-full cursor-grab active:cursor-grabbing"
              style={{
                scaleX: reduceMotion ? 1 : (scaleX as unknown as number),
                scaleY: reduceMotion ? 1 : (scaleY as unknown as number),
                rotate: reduceMotion ? 0 : (rotate as unknown as number),
                filter: glow as unknown as string,
                transformOrigin: "50% 72%",
                willChange: "transform, filter",
              }}
            >
              <LemonSVG highlight={reduceMotion ? undefined : highlightSpring} />
            </motion.button>
          </motion.div>

          {/* Completion burst — radial glow blooms from lemon center right
              before the page-level curtain kicks in, so the finish reads as
              a soft flare rather than a hard cut. */}
          <AnimatePresence>
            {(phase === "burst" || phase === "reveal") && (
              <motion.div
                key="lemon-flare"
                aria-hidden
                className="absolute inset-0 pointer-events-none flex items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              >
                <motion.div
                  className="rounded-full"
                  initial={{ scale: 0.2, opacity: 0.9 }}
                  animate={{ scale: 3.2, opacity: 0 }}
                  transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
                  style={{
                    width: 260,
                    height: 260,
                    background:
                      "radial-gradient(circle, hsl(60 100% 92% / 0.95) 0%, hsl(48 100% 60% / 0.75) 35%, hsl(35 95% 55% / 0.25) 65%, transparent 75%)",
                    filter: "blur(2px)",
                    willChange: "transform, opacity",
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Juice droplet */}
          <motion.div
            aria-hidden
            className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
            style={{ bottom: "-24px" }}
            animate={{
              opacity: progress > 0.5 ? 1 : 0,
              y: progress > 0.5 ? 20 + progress * 40 : 0,
              scale: 0.4 + progress * 0.9,
            }}
            transition={{
              y: { type: "spring", stiffness: 120, damping: 22, mass: 1 },
              scale: { type: "spring", stiffness: 140, damping: 24, mass: 1 },
              opacity: { duration: 0.35, ease: "easeOut" },
            }}
          >
            <svg width="22" height="30" viewBox="0 0 22 30" fill="none">
              <path
                d="M11 2 C 4 14, 2 20, 11 28 C 20 20, 18 14, 11 2 Z"
                fill="#FF8A24"
                stroke="#C24810"
                strokeWidth="0.6"
                opacity="0.9"
              />
              <ellipse cx="8" cy="12" rx="2" ry="4" fill="#FFD9A8" opacity="0.75" />
            </svg>
          </motion.div>

          {/* Halo droplets — orbit around the lemon, scale/fade with squeeze */}
          <HaloDroplets spring={dropletSpring} />
        </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex flex-col items-center gap-3"
        >
          <PinchIndicator active={progress > 0} />
          <motion.p
            animate={
              progress > 0
                ? { opacity: 1, color: "hsl(var(--primary))" }
                : { opacity: [0.55, 1, 0.55] }
            }
            transition={
              progress > 0
                ? { duration: 0.25 }
                : { duration: 2.4, repeat: Infinity, ease: "easeInOut" }
            }
            className="text-[11px] sm:text-xs font-semibold tracking-[0.28em] uppercase text-muted-foreground"
          >
            {t("Tieni premuto per spremere", "Press & hold to squeeze", "Mantén presionado para exprimir")}
          </motion.p>
        </motion.div>
      </main>
    </div>
  );
};

// Vector reconstruction of the brand lemon: the two glyph paths come straight
// from the uploaded logo (with the embedded raster fill replaced by a live
// gradient), so we get sharp scaling at any size and can sample matching hues
// for the halo droplets.
export const LemonSVG = ({ highlight: _highlight, className }: { highlight?: MotionValue<number>; className?: string }) => {
  void _highlight;
  return (
    <svg
      width="280"
      height="291"
      viewBox="-17 -1 622 646"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className ?? "max-w-[68vw] block select-none"}
      aria-hidden
    >
      <defs>
        <linearGradient id="lemonBodyGrad" x1="0%" y1="20%" x2="100%" y2="90%">
          <stop offset="0%" stopColor="#E22515" />
          <stop offset="30%" stopColor="#FF5A1E" />
          <stop offset="60%" stopColor="#FF5A1E" />
          <stop offset="100%" stopColor="#FFA02F" />
        </linearGradient>
        <linearGradient id="lemonLeafGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F13820" />
          <stop offset="100%" stopColor="#C21A0F" />
        </linearGradient>
      </defs>
      {/* Main body (with the mouth cut-out) — path from uploaded logo */}
      <path
        d="M268.958 110.559C387.156 102.276 499.721 188.979 514.242 307.931C515.562 318.681 514.506 333.044 516.443 342.824C518.203 351.371 527.004 360.27 530.788 368.465C546.806 403.798 531.668 443.978 494.44 456.137C486.959 458.604 479.39 458.34 472.79 460.102C463.813 462.481 430.369 493.938 416.551 502.308C277.231 586.632 81.9365 494.554 69.7911 330.576C68.735 316.302 72.0793 295.331 69.439 282.379C68.2069 276.563 60.022 268.016 57.1177 261.937C38.2835 222.903 63.1023 177.26 106.139 172.943C112.916 172.238 119.605 174.088 126.382 171.797C133.158 169.506 142.84 158.933 149.088 154.615C184.908 130.032 225.129 113.643 269.046 110.559H268.958ZM208.407 376.483C261.477 429.88 352.568 441.687 414.439 396.132C429.048 385.383 439.434 369.346 421.744 354.807C405.638 341.591 395.429 355.16 382.227 362.385C316.66 398.335 234.282 369.963 213.16 296.741C209.903 285.286 211.927 272.951 199.782 265.902C181.212 255.064 163.61 272.07 165.282 291.895C167.57 320.355 188.517 356.57 208.407 376.483Z"
        fill="url(#lemonBodyGrad)"
      />
      {/* Detached leaf / drop at top-left */}
      <path
        d="M27.1725 0.00350302C31.749 -0.172723 45.8307 6.34762 50.4952 8.81478C97.2286 34.015 117.295 92.0813 87.2835 138.429C77.6903 153.232 74.346 152.879 58.5041 146.359C4.99395 124.331 -17.1846 61.4181 14.8511 11.7225C17.6674 7.31688 21.7159 0.267843 27.2605 0.0916178L27.1725 0.00350302Z"
        fill="url(#lemonLeafGrad)"
      />
    </svg>
  );
};

// Hue palette sampled from the lemon vector: left side reads deep red, the
// core is a warm orange, the right edge tips toward a saturated yellow-orange.
// We map each halo droplet's origin angle to the closest zone so the droplet
// looks like it was squeezed out of that region of the fruit.
const dropletPaletteForAngle = (angle: number) => {
  // Normalise to [-180, 180]
  const a = ((angle + 540) % 360) - 180;
  // t = 0 → far left (deep red), 1 → far right (orange-yellow)
  const t = (Math.cos((a * Math.PI) / 180) + 1) / 2;
  // Slight vertical tint: upper droplets ride the brighter shoulder of the
  // gradient, lower ones sit in the shaded belly.
  const vertical = (Math.sin((a * Math.PI) / 180) + 1) / 2; // 0 top, 1 bottom
  const hue = 6 + t * 22 - vertical * 2; // 4..28
  const light = 46 + t * 10 - vertical * 4;
  return {
    fill: `hsl(${hue} 92% ${light}%)`,
    stroke: `hsl(${Math.max(0, hue - 4)} 90% ${Math.max(28, light - 16)}%)`,
    inner: `hsl(${hue + 8} 100% ${Math.min(88, light + 32)}%)`,
  };
};

type HaloDropletConfig = {
  angle: number; // degrees; 0 = right, 90 = down, -90 = up
  radius: number; // px from lemon center
  delay: number; // stagger 0..1 applied to spring threshold
  sizeScale: number;
  hideOnMobile?: boolean;
};

const HALO_DROPLETS: HaloDropletConfig[] = [
  { angle: -120, radius: 182, delay: 0.0, sizeScale: 1.0 },
  { angle: -65, radius: 176, delay: 0.07, sizeScale: 0.88 },
  { angle: -15, radius: 198, delay: 0.14, sizeScale: 1.12, hideOnMobile: true },
  { angle: 165, radius: 172, delay: 0.04, sizeScale: 0.92 },
  { angle: 110, radius: 188, delay: 0.11, sizeScale: 1.05, hideOnMobile: true },
  { angle: 50, radius: 192, delay: 0.19, sizeScale: 0.78 },
  // Smaller satellite droplets, farther out, filling the gaps
  { angle: -90, radius: 236, delay: 0.22, sizeScale: 0.52 },
  { angle: -40, radius: 244, delay: 0.28, sizeScale: 0.46, hideOnMobile: true },
  { angle: 140, radius: 240, delay: 0.25, sizeScale: 0.5 },
  { angle: 80, radius: 250, delay: 0.32, sizeScale: 0.42, hideOnMobile: true },
];

type DropletPalette = { fill: string; stroke: string; inner: string };

const DropletSVG = ({ palette }: { palette: DropletPalette }) => (
  <svg width="22" height="30" viewBox="0 0 22 30" fill="none">
    <path
      d="M11 2 C 4 14, 2 20, 11 28 C 20 20, 18 14, 11 2 Z"
      fill={palette.fill}
      stroke={palette.stroke}
      strokeWidth="0.6"
      opacity="0.92"
    />
    <ellipse cx="8" cy="12" rx="2" ry="4" fill={palette.inner} opacity="0.75" />
  </svg>
);

const HaloDroplet = ({
  spring,
  angle,
  radius,
  delay,
  sizeScale,
  hideOnMobile,
}: HaloDropletConfig & { spring: MotionValue<number> }) => {
  const rad = (angle * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  // droplet points outward: its "tip" (top of svg) should face the lemon center,
  // so rotate so the tail points away from center. SVG tip is up (angle -90),
  // so rotation = angle + 90 aligns the tip toward center as it flies outward.
  const rotateDeg = angle + 90;

  const progress = (v: number) =>
    Math.max(0, Math.min(1, (v - delay) / Math.max(0.001, 1 - delay)));

  // Smooth ease-out-quint for centrifugal flight — accelerates gently and
  // settles softly, no snap at the tail end.
  const ease = (t: number) => 1 - Math.pow(1 - t, 3.2);

  const x = useTransform(spring, (v) => cos * radius * ease(progress(v)) - 11 * sizeScale);
  const y = useTransform(spring, (v) => sin * radius * ease(progress(v)) - 15 * sizeScale);

  const opacity = useTransform(spring, (v) => {
    const t = progress(v);
    // Fade in quickly leaving the lemon, then gently fade out as the droplet
    // reaches the end of its flight — no hard pop-off.
    const fadeIn = Math.min(1, t * 2.4);
    const fadeOut = t > 0.75 ? 1 - (t - 0.75) / 0.25 : 1;
    return fadeIn * fadeOut;
  });

  const scale = useTransform(spring, (v) => {
    const t = progress(v);
    // Ease the scale-up so growth feels organic rather than linear.
    const grow = 0.25 + (1 - Math.pow(1 - t, 2)) * 0.95;
    return grow * sizeScale;
  });

  // Subtle rotation drift for extra life — each droplet swings a hair as it flies.
  const rotateOffset = useTransform(spring, (v) => {
    const t = progress(v);
    return Math.sin(t * Math.PI) * (angle % 2 === 0 ? 6 : -6);
  });

  return (
    <motion.div
      className={`absolute left-1/2 top-1/2 ${hideOnMobile ? "hidden sm:block" : ""}`}
      style={{
        x,
        y,
        opacity,
        scale,
        willChange: "transform, opacity",
      }}
    >
      <motion.div style={{ rotate: rotateOffset }}>
        <div style={{ transform: `rotate(${rotateDeg}deg)` }}>
          <DropletSVG palette={dropletPaletteForAngle(angle)} />
        </div>
      </motion.div>
    </motion.div>
  );
};

const HaloDroplets = ({ spring }: { spring: MotionValue<number> }) => {
  return (
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: -1 }}
    >
      {HALO_DROPLETS.map((d, i) => (
        <HaloDroplet key={i} spring={spring} {...d} />
      ))}
    </div>
  );
};

const PinchIndicator = ({ active }: { active: boolean }) => (
  <div className="relative h-6 w-16 flex items-center justify-center">
    <motion.div
      className="absolute w-2 h-2 rounded-full bg-primary"
      animate={
        active
          ? { x: -2, opacity: 0.9, scale: 1.15 }
          : { x: [-18, -5, -18], opacity: [0.4, 1, 0.4], scale: [0.9, 1.1, 0.9] }
      }
      transition={
        active
          ? { type: "spring", stiffness: 260, damping: 20 }
          : { duration: 1.8, repeat: Infinity, ease: [0.4, 0, 0.2, 1] }
      }
    />
    <motion.div
      className="absolute w-2 h-2 rounded-full bg-primary"
      animate={
        active
          ? { x: 2, opacity: 0.9, scale: 1.15 }
          : { x: [18, 5, 18], opacity: [0.4, 1, 0.4], scale: [0.9, 1.1, 0.9] }
      }
      transition={
        active
          ? { type: "spring", stiffness: 260, damping: 20 }
          : { duration: 1.8, repeat: Infinity, ease: [0.4, 0, 0.2, 1] }
      }
    />
    {/* Center spark that pops when the two dots meet */}
    <motion.div
      className="absolute w-1 h-1 rounded-full bg-primary"
      animate={active ? { scale: [0, 1.6, 0], opacity: [0, 0.9, 0] } : { scale: 0, opacity: 0 }}
      transition={{ duration: 0.5, repeat: active ? Infinity : 0, ease: "easeOut" }}
    />
  </div>
);

export default LandingPage;